/* AI 작업 — 템플릿으로 만들기(글 실시간 생성) · AI 편집자 채팅 · 사진 편집 · 표시하고 고치기
   1) 편집자: 페이지의 요소 목록(t1·i2 같은 짧은 id)을 보여주고 "한 줄에 명령 하나"로 답하게 한다.
      스트리밍으로 받으면서 줄이 하나 완성될 때마다 바로 페이지에 적용한다.
   2) 글 내용은 편집자가 직접 쓰지 않고 "고치기 t4 요청"만 낸다. 실제 문구는 문체 프롬프트로 따로 써서
      그 텍스트 상자에 한 글자씩 흘려 넣는다. */
import { showcaseApi, errorMessage, isAbort, streamText, type UsageLite } from '../api/showcaseApi';
import { LAYOUT_SHORT, ROOM_LABEL, ROOM_LAYOUTS, STYLES, roomLabel, toCode } from '../model/constants';
import { refOf } from '../model/images';
import { DEFAULT_ROOM_LAYOUT, isHeading, isRoomPage, layoutRoomPage, roomParts, textRole } from '../model/layout';
import { buildTemplatePages, makeBuildCtx, type BuildCtx, type PhotoMeta, type Template } from '../model/templates';
import { PAGE_H, newId, pageId, type El, type ImageRef, type Page } from '../model/types';
import {
  ed, bringToFront, currentTemplate, locateElement, saveNow, sendToBack, showPage, startNewCase, toast, undoLast, withUndo,
  type ChatLog, type ChatMsg, INTRO,
} from '../store/editor';
import {
  AGENT_INSTRUCTIONS, AGENT_INSTRUCTIONS_SCOPED, buildElementEditPrompt, buildHeadingEditPrompt, buildImageEditPrompt,
  buildImageMatchPrompt, buildMarkupPrompt, cleanWriterText, genPrompt, withPhotosAndGuide, writtenLine, WRITER_PERSONA, type Mark,
} from './prompts';

/* ── 공통 ── */
const reqKey = (p: string) => p + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
function addUsage(m: ChatMsg | null, u: UsageLite | null | undefined) {
  ed.usageTick++;
  if (!m || !u) return;
  const x = (m.usage ||= { total: 0, input: 0, output: 0, thinking: 0, images: 0, calls: 0 });
  x.total += u.total || 0; x.input += u.input || 0; x.output += u.output || 0; x.thinking += u.thinking || 0; x.images += u.images || 0; x.calls += 1;
}
function setBusy(on: boolean) {
  ed.busy = on;
  if (!on) ed.abort = null;
  ed.bump();
}
const signal = () => ed.abort?.signal;
export function liveSetText(el: El, text: string) {
  el.text = text;
  if (text) ed.thinking.delete(el.id); // 첫 글자가 오면 "생각하는 중" 안내를 걷는다
  ed.bump();
}
/** what: 무엇을 쓰는지(예: 7페이지 욕실 소개 글) — 글이 오기 전까지 글 상자에 "AI가 생각하는 중" 안내로 보인다 */
function setTyping(el: El, on: boolean, what?: string) {
  if (on) { ed.typing.add(el.id); ed.thinking.set(el.id, what || '글'); }
  else { ed.typing.delete(el.id); ed.thinking.delete(el.id); }
  ed.bump();
}
function flashElement(el: El) {
  ed.flash.add(el.id);
  setTimeout(() => { ed.flash.delete(el.id); ed.bump(); }, 1400);
}
const isEditedPhoto = (e: El) => e.type === 'image' && !!e.aiImgId;
/** 사진 이름 — 그 페이지의 공간 이름(인디포토 공간 태그) */
function photoName(e: El) {
  const pi = locateElement(e), p = ed.pages[pi];
  return p?.room ? roomLabel(p.room) : '사진';
}
/** 그 페이지 사진 — 큰 사진부터 6장 */
function pageImageRefs(pi: number): ImageRef[] {
  const p = ed.pages[pi];
  if (!p) return [];
  const seen = new Set<string>(), out: ImageRef[] = [];
  p.elements.filter((e) => e.type === 'image' && e.src).sort((a, b) => b.w * b.h - a.w * a.h).forEach((e) => {
    const r = refOf(e);
    const k = JSON.stringify(r);
    if (r && !seen.has(k) && out.length < 6) { seen.add(k); out.push(r); }
  });
  return out;
}

/* ── 문체 가이드 — 템플릿 스타일마다 서버가 7일 보관 ── */
let styleGuide: { key: string; guide: string; cached: boolean } | null = null;
async function ensureStyleGuide(tpl: Template) {
  const key = toCode(tpl.style) || 'BASIC';
  if (styleGuide && styleGuide.key === key) return styleGuide;
  const st = tpl.style ? STYLES[tpl.style] : null;
  const g = await showcaseApi.styleGuide({ style: key, label: st?.label || '', tone: st?.tone || '' }, signal());
  styleGuide = { key, guide: g.guide, cached: g.cached };
  return styleGuide;
}
const guideText = () => styleGuide?.guide || null;

/* ============================================================
   템플릿으로 만들기 — 사진은 즉시 배치, gen 글만 AI가 차례로 써서 실시간으로 흘려 넣는다
   ============================================================ */
export interface GenSource {
  grouped: Record<string, string[]>;
  site: string;
  siteId: number | null;
  collection: boolean;
  meta: Map<string, PhotoMeta>;
}
function genLabel(e: El) {
  const pi = locateElement(e), g = e.gen!;
  const what = g.kind === 'overview' ? '현장 소개 글' : g.kind === 'detail' ? `${roomLabel(g.room)} 디테일 설명` : `${roomLabel(g.room)} 소개 글`;
  return `${pi + 1}페이지 ${what}`;
}
function genImages(e: El, pi: number, ctx: BuildCtx): ImageRef[] {
  if (e.gen?.kind === 'overview') {
    const only = e.gen.focus || ctx.single; // 한 공간만 소개하면 그 공간 사진을 여러 장 보여준다
    const srcs = only ? (ctx.grouped[only] || []).slice(0, 6) : ctx.rooms.map((r) => (ctx.grouped[r] || [])[0]).filter(Boolean).slice(0, 6);
    return srcs.map((s) => refOf(s)).filter((r): r is ImageRef => !!r);
  }
  return pageImageRefs(pi);
}
const cleanGen = (s: string, e: El) => {
  const t = cleanWriterText(s, false);
  return e.gen?.kind === 'detail' ? t.split('\n')[0].trim() : t;
};

export async function generateFromTemplate(tpl: Template, src: GenSource) {
  if (ed.busy) { toast('AI가 아직 작업 중이에요. 끝나거나 ■ 중지한 뒤에 다시 해주세요.'); return; }
  if (!Object.keys(src.grouped).length) { toast('고른 사진이 없어요'); return; }
  if (tpl.focus && !src.grouped[tpl.focus]) { toast(`${roomLabel(tpl.focus)} 사진이 없어서 '${tpl.name}'은 쓸 수 없어요`); return; }
  const ctx = makeBuildCtx(src.grouped, src.site, false);
  ctx.collection = src.collection;
  ctx.meta = src.meta;
  const pages = buildTemplatePages(tpl, ctx);
  try {
    await startNewCase({ pages, title: `${ctx.site} 시공사례`, templateId: tpl.id, siteId: src.siteId, bundle: src.collection });
  } catch (e) {
    toast('시공사례를 만들지 못했어요 — ' + errorMessage(e));
    return;
  }
  ed.dockOpen = true;
  ed.chat.push({ role: 'system', text: INTRO });
  const jobs: El[] = [];
  ed.pages.forEach((p) => p.elements.forEach((e) => { if (e.gen) jobs.push(e); }));
  const aiMsg: ChatMsg = {
    role: 'ai',
    text: `'${tpl.name}' 템플릿으로 ${ctx.site} 시공사례를 만들고 있어요. 고른 사진 ${ctx.all.length}장을 공간 태그대로 배치했고, 글 ${jobs.length}곳을 AI가 차례로 씁니다.`,
    log: jobs.map((e) => ({ state: 'wait', text: genLabel(e) } as ChatLog)),
    status: '',
  };
  const jobLog = aiMsg.log!.slice();
  ed.chat.push(aiMsg);
  ed.abort = new AbortController();
  setBusy(true);
  let stopped = false;
  // 먼저 구글 검색으로 실제 시공사례 글을 읽고 문체 가이드를 만든다 (스타일마다 한 번, 이후 7일은 바로)
  const gEntry: ChatLog = { state: 'run', text: '인터넷 시공사례 글 참고하기 (구글 검색)' };
  aiMsg.log!.unshift(gEntry);
  aiMsg.status = '실제 인테리어 업체 시공사례 글을 찾아 읽는 중… (처음 한 번 1분 안팎)';
  ed.bump();
  try {
    const g = await ensureStyleGuide(tpl);
    gEntry.state = 'ok';
    gEntry.text = '인터넷 시공사례 글 참고' + (g.cached ? ' (저장해 둔 문체 가이드)' : '');
  } catch (err) {
    if (isAbort(err)) stopped = true;
    gEntry.state = 'skip';
    gEntry.text += isAbort(err) ? ' — 중지' : ` — 건너뜀(${errorMessage(err)})`;
  }
  ed.usageTick++;
  ed.bump();
  const written: string[] = [];
  try {
    for (let i = 0; i < jobs.length; i++) {
      const e = jobs[i], entry = jobLog[i];
      if (stopped) { liveSetText(e, e.sample || ''); entry.state = 'skip'; entry.text += ' — 중지(예시 문구)'; continue; }
      const pi = locateElement(e);
      if (pi < 0) { entry.state = 'skip'; entry.text += ' — 그새 지워진 글'; continue; }
      entry.state = 'run';
      aiMsg.status = `글을 쓰는 중… (${i + 1}/${jobs.length})`;
      showPage(pi);
      setTyping(e, true, entry.text);
      try {
        const imgs = genImages(e, pi, ctx);
        const prompt = withPhotosAndGuide(genPrompt(e.gen!, ctx, tpl), imgs.length, guideText()) + writtenLine(written);
        const r = await streamText({ projectId: ed.projectId, aiType: 'WRITER', prompt, images: imgs, temperature: 0.6, reqKey: reqKey('g') },
          (partial) => liveSetText(e, cleanGen(partial, e)), signal());
        addUsage(aiMsg, r.usage);
        const final = cleanGen(r.text, e);
        if (!final) throw new Error('빈 응답');
        liveSetText(e, final);
        entry.state = 'ok';
        if (e.gen?.kind !== 'detail') written.push(final);
      } catch (err) {
        liveSetText(e, e.sample || '');
        if (isAbort(err)) { stopped = true; entry.state = 'skip'; entry.text += ' — 중지(예시 문구)'; }
        else { entry.state = 'fail'; entry.text += ` — 실패, 예시 문구로 채움 (${errorMessage(err)})`; }
      } finally {
        setTyping(e, false);
      }
    }
  } finally {
    ed.pages.forEach((p) => p.elements.forEach((e) => { delete e.gen; delete e.sample; }));
    aiMsg.status = stopped
      ? '중지했어요. 남은 글은 예시 문구로 채웠어요 — 고치고 싶은 글은 요소 선택으로 첨부해서 다시 써달라고 하세요.'
      : '완료! 고치고 싶은 곳은 이 채팅으로 말하거나, 요소 선택으로 골라서 요청하세요.';
    ed.cur = 0;
    setBusy(false);
    void saveNow(true);
  }
}

/* ============================================================
   AI 편집자
   ============================================================ */
interface AgentCtx {
  alias: Record<string, El>;
  idOf: Map<El, string>;
  pageRefs: Page[];
  lines: string[];
  scoped: boolean;
  attached: El[];
}
export function attachLabel(e: El) {
  const pi = locateElement(e);
  const kind = e.type === 'text' ? textRole(e) : e.type === 'image' ? (isRoomPage(ed.pages, pi) && e === roomParts(ed.pages[pi]).main ? '대표 사진' : '사진') : '도형';
  const t = (e.text || '').trim();
  const detail = e.type === 'text' ? `"${t.replace(/\s+/g, ' ').slice(0, 18)}${t.length > 18 ? '…' : ''}"` : e.type === 'image' ? photoName(e) : '';
  return `${kind} · ${pi + 1}p ${detail}`.trim();
}
function buildScopedContext(attached: El[]): AgentCtx {
  const ctx: AgentCtx = { alias: {}, idOf: new Map(), pageRefs: ed.pages.slice(), lines: [], scoped: true, attached: attached.slice() };
  const seq = { text: 0, image: 0, shape: 0 }, pre = { text: 't', image: 'i', shape: 's' };
  attached.forEach((e) => {
    const pi = locateElement(e), p = ed.pages[pi];
    const a = pre[e.type] + ++seq[e.type];
    ctx.alias[a] = e; ctx.idOf.set(e, a);
    const head = p.elements.find((x) => x.role === 'heading') || p.elements.find(isHeading);
    const where = `${pi + 1}페이지${isRoomPage(ed.pages, pi) && head && head !== e ? ' · ' + (head.text || '').trim().slice(0, 10) : ''}`;
    if (e.type === 'text') {
      const t = (e.text || '').trim().replace(/\s+/g, ' ');
      ctx.lines.push(`${a} ${textRole(e)} ${Math.round(e.fontSize || 16)}px${e.bold ? ' 굵게' : ''}${e.align === 'center' ? ' 가운데' : e.align === 'right' ? ' 오른쪽' : ''} (${where}) "${t.length > 400 ? t.slice(0, 400) + '…' : t}"`);
    } else if (e.type === 'image') {
      ctx.lines.push(`${a} ${isRoomPage(ed.pages, pi) && e === roomParts(p).main ? '대표사진' : '사진'} (${where}) "${photoName(e)}"${isEditedPhoto(e) ? ' (AI로 고친 사진)' : ''}`);
    } else ctx.lines.push(`${a} 도형 (${where})`);
  });
  return ctx;
}
function buildAgentContext(): AgentCtx {
  const ctx: AgentCtx = { alias: {}, idOf: new Map(), pageRefs: ed.pages.slice(), lines: [], scoped: false, attached: [] };
  const seq = { text: 0, image: 0, shape: 0 }, pre = { text: 't', image: 'i', shape: 's' };
  ed.pages.forEach((p, pi) => {
    const head = p.elements.find((e) => e.role === 'heading') || p.elements.find(isHeading);
    const isCover = p.kind === 'cover' || (pi === 0 && !isRoomPage(ed.pages, pi));
    const tags = [`${pi + 1}페이지`, isCover ? '표지' : isRoomPage(ed.pages, pi) && head ? (head.text || '').trim().slice(0, 10) : ''];
    if (isRoomPage(ed.pages, pi) && p.layout) tags.push(LAYOUT_SHORT[p.layout]);
    ctx.lines.push('[' + tags.filter(Boolean).join(' · ') + ']');
    const els = p.elements.slice().sort((a, b) => a.y - b.y || a.x - b.x);
    if (!els.length) ctx.lines.push('(비어 있음)');
    els.forEach((e) => {
      const a = pre[e.type] + ++seq[e.type];
      ctx.alias[a] = e; ctx.idOf.set(e, a);
      if (e.type === 'text') {
        const t = (e.text || '').trim().replace(/\s+/g, ' ');
        ctx.lines.push(`${a} ${textRole(e)} ${Math.round(e.fontSize || 16)}px${e.bold ? ' 굵게' : ''}${e.align === 'center' ? ' 가운데' : e.align === 'right' ? ' 오른쪽' : ''} "${t.length > 45 ? t.slice(0, 45) + '…' : t}"`);
      } else if (e.type === 'image') {
        const room = isRoomPage(ed.pages, pi) ? (e === roomParts(p).main ? '대표사진' : '서브사진') : '사진';
        ctx.lines.push(`${a} ${room} "${photoName(e)}"${isEditedPhoto(e) ? ' (AI로 고친 사진)' : ''}`);
      } else ctx.lines.push(`${a} 도형`);
    });
  });
  return ctx;
}
function buildAgentPrompt(ctx: AgentCtx, msg: string) {
  const past = ed.chat.filter((m) => (m.role === 'user' || m.role === 'ai') && m.text).slice(-5, -1)
    .map((m) => (m.role === 'user' ? '요청: ' : '답: ') + m.text.split('\n')[0].slice(0, 120)).join('\n');
  const tail = (past ? `[이전 대화]\n${past}\n\n` : '') + `요청: ${msg}\n답:`;
  if (ctx.scoped) {
    const head = `[첨부한 요소] 사용자가 페이지에서 직접 골라 첨부한 요소입니다. "이것", "이 글", "이 사진"은 이 요소를 뜻합니다.`;
    return `${AGENT_INSTRUCTIONS_SCOPED}\n${head}\n${ctx.lines.join('\n')}\n\n` + tail;
  }
  const head = `[페이지] 총 ${ed.pages.length}페이지 · 지금 보고 있는 페이지: ${ed.cur + 1}페이지 (페이지를 말하지 않으면 이 페이지)`;
  return `${AGENT_INSTRUCTIONS}\n${head}\n${ctx.lines.join('\n')}\n\n` + tail;
}

/* ── 편집자 출력 한 줄 → 명령 ── */
const AGENT_OPS = ['고치기', '크기', '정렬', '굵게', '색', '삭제', '확대', '대표사진', '사진수정', '사진원복', '사진맞춤', '앞으로', '뒤로', '레이아웃', '배경', '글추가', '새페이지', '페이지삭제', '이동'];
const OP_ALIASES: Record<string, string> = {
  '수정': '고치기', '다시쓰기': '고치기', '글': '고치기', '문구': '고치기', '새글': '고치기', '글자크기': '크기', '텍스트추가': '글추가', '페이지추가': '새페이지',
  '페이지이동': '이동', '대표': '대표사진', '사진편집': '사진수정', '이미지수정': '사진수정', '이미지편집': '사진수정', '합성': '사진수정', '사진고치기': '사진수정',
  '원복': '사진원복', '사진복구': '사진원복', '원본': '사진원복', '맞춤': '사진맞춤', '가구맞춤': '사진맞춤',
};
interface Cmd { op: string; args: string; raw: string }
function parseAgentLine(line: string): Cmd | null {
  const s = line.trim().replace(/^[-*•·>]\s*|^\d+[.)]\s*/, '').replace(/`/g, '');
  const m = s.match(/^([^\s:：]+)[:：]?\s*(.*)$/);
  if (!m) return null;
  const op = OP_ALIASES[m[1]] || m[1];
  return AGENT_OPS.includes(op) ? { op, args: m[2].trim(), raw: s } : null;
}
const COLOR_WORDS: [RegExp, string][] = [
  [/흰|화이트|하얀/, '#FFFFFF'], [/검정|검은|블랙|어두운|남색|네이비/, '#1B1F4F'], [/회색|그레이/, '#F5F5F5'], // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
  [/베이지|크림|아이보리/, '#FDF3EC'], [/초록|그린|민트/, '#E9F5EE'], [/파랑|블루|하늘|연보라/, '#EBEDFA'], // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
];
const TEXT_COLORS: [RegExp, string][] = [
  [/빨|레드/, '#DC2626'], [/주황|오렌지/, '#EA580C'], [/노랑|노란|옐로/, '#CA8A04'], [/초록|그린|민트/, '#059669'], // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
  [/파랑|파란|블루|하늘/, '#2563EB'], [/남색|네이비|브랜드/, '#1B1F4F'], [/보라|퍼플/, '#7C3AED'], [/회색|그레이/, '#6B7280'], // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
  [/베이지|브라운|갈색/, '#8B6B4A'], [/흰|하얀|화이트/, '#FFFFFF'], [/검|블랙/, '#111827'], // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
];
function pickColor(word: string, palette: [RegExp, string][]) {
  const hex = word.match(/#[0-9a-fA-F]{3,8}\b/);
  if (hex) return hex[0];
  const hit = palette.find(([re]) => re.test(word));
  return hit ? hit[1] : null;
}
function describeEl(el: El, pi: number) {
  const kind = el.type === 'text' ? textRole(el) : el.type === 'image' ? (isRoomPage(ed.pages, pi) && el === roomParts(ed.pages[pi]).main ? '대표 사진' : '사진') : '도형';
  return `${pi + 1}페이지 ${kind}`;
}
/* 문장에 나온 페이지들(번호·표지·방 이름)을 나온 순서대로 */
function pageRefsIn(text: string) {
  const hits: { pos: number; i: number }[] = [];
  const add = (pos: number, i: number) => { if (i >= 0 && i < ed.pages.length) hits.push({ pos, i }); };
  for (const m of text.matchAll(/(\d+)\s*(?:페이지|쪽|p\b)/gi)) add(m.index ?? 0, +m[1] - 1);
  const cover = text.search(/표지|커버/);
  if (cover >= 0) add(cover, 0);
  Object.values(ROOM_LABEL).forEach((label) => {
    const pos = text.indexOf(label);
    if (pos < 0) return;
    add(pos, ed.pages.findIndex((p) => p.elements.some((e) => e.type === 'text' && (e.role === 'heading' || isHeading(e)) && (e.text || '').trim() === label)));
  });
  return hits.sort((a, b) => a.pos - b.pos).map((h) => h.i).filter((v, k, arr) => arr.indexOf(v) === k);
}
const ORDINALS: Record<string, number> = { '첫': 1, '두': 2, '세': 3, '네': 4, '다섯': 5, '여섯': 6, '일곱': 7, '여덟': 8, '아홉': 9 };
function inferTarget(ctx: AgentCtx, op: string, msg: string, el: El | null): El | null {
  const textOps = ['고치기', '크기', '정렬', '굵게', '색'];
  const fits = (e: El) => (textOps.includes(op) ? e.type === 'text' : op === '대표사진' || op === '사진수정' || op === '사진맞춤' ? e.type === 'image' : op === '확대' ? e.type !== 'text' : true);
  if (ctx.scoped) {
    if (el && fits(el)) return null;
    const cands = ctx.attached.filter(fits);
    return cands.length === 1 ? cands[0] : null;
  }
  // 요청을 절 단위로 나눠서, 이 명령과 관련된 절에서 페이지와 대상을 찾는다
  const clauses = msg.split(/그리고|하고|하며|,|\s고\s|(?<=[바꾸넣빼줄키옮두])고\s/).map((s) => s.trim()).filter(Boolean);
  const clause = clauses.length === 1 ? clauses[0] : msg;
  const refs = pageRefsIn(clause), allRefs = pageRefsIn(msg);
  const pi = refs.length ? refs[0] : allRefs.length === 1 ? allRefs[0] : ed.cur;
  const page = ed.pages[pi];
  if (!page) return null;
  const ordOf = () => {
    const ord = clause.match(/(\d+|첫|두|세|네|다섯|여섯|일곱|여덟|아홉)\s*(?:번째|번)\s*사진/);
    return ord ? (/\d/.test(ord[1]) ? +ord[1] : ORDINALS[ord[1]]) : 0;
  };
  if (op === '대표사진') {
    const n = ordOf();
    if (!n) return null;
    const { main, subs } = roomParts(page);
    return [main, ...subs].filter(Boolean)[n - 1] || null;
  }
  if (op === '사진수정' || op === '사진맞춤') {
    if (el && ctx.idOf.has(el) && fits(el)) return null;
    const { main, subs } = roomParts(page);
    const list = [main, ...subs].filter((x): x is El => !!x);
    const n = ordOf();
    if (n) return list[n - 1] || null;
    return list[0] || page.elements.find((e) => e.type === 'image') || null;
  }
  if (el && ctx.idOf.has(el) && fits(el)) return null;
  if (!textOps.includes(op)) return null;
  const { heading, body } = roomParts(page);
  if (/제목/.test(clause)) return heading || null;
  if (/(문구|글|본문|내용|설명|문단)/.test(clause) || op === '고치기') return body || null;
  return null;
}
function agentPageIndex(ctx: AgentCtx, tok: string) {
  const n = parseInt(String(tok).replace(/[^\d]/g, ''), 10);
  if (!n) return /(지금|현재|이)/.test(tok) ? ed.cur : -1;
  const p = ctx.pageRefs[n - 1];
  return p ? ed.pages.indexOf(p) : -1;
}

type Task =
  | { kind: 'rewrite'; el: El; pi: number; how: string }
  | { kind: 'add'; pi: number; heading: boolean; how: string; el?: undefined }
  | { kind: 'image'; el: El; pi: number; how: string; refEl?: El | null; marked?: string; prompt?: string }
  | { kind: 'match'; el: El; ref: El; pi: number; how: string; refEl?: El | null };
type CmdResult = { ok: boolean; text: string; later?: undefined } | { later: true; task: Task; text: string; ok?: undefined };

/** 사진 원복 — 기억해 둔 맨 처음 사진으로 */
export function restorePhoto(el: El) {
  if (!el.origSrc) return false;
  if (el.aiImgId) void showcaseApi.confirmImage(el.aiImgId, false).catch(() => undefined);
  el.src = el.origSrc; el.photoId = el.origPhotoId; el.externalId = el.origExternalId;
  delete el.aiImgId; delete el.origSrc; delete el.origPhotoId; delete el.origExternalId;
  return true;
}

function applyAgentCommand(ctx: AgentCtx, cmd: Cmd, msg: string): CmdResult {
  const toks = cmd.args.split(/\s+/).filter(Boolean);
  const a0 = toks[0] || '', a1 = toks[1] || '';
  const idTok = (t: string) => /^[tis]\d+[,.)]?$/.test(t);
  let ei = toks.findIndex((t) => idTok(t) && ctx.alias[t.replace(/[^\w]/g, '')]);
  if (ei < 0) ei = toks.findIndex(idTok);
  let el: El | null = ei >= 0 ? ctx.alias[toks[ei].replace(/[^\w]/g, '')] || null : null;
  const inferred = inferTarget(ctx, cmd.op, msg, el);
  if (inferred) el = inferred;
  const after = ei >= 0 ? toks.slice(ei + 1) : [];
  const v = after[0] || '', vAll = after.join(' ');
  const needEl = ['고치기', '크기', '정렬', '굵게', '색', '삭제', '확대', '대표사진', '사진수정', '사진원복', '사진맞춤', '앞으로', '뒤로'].includes(cmd.op);
  if (ctx.scoped && !needEl) return { ok: false, text: `${cmd.raw} — 첨부한 요소에 대한 명령이 아니라 건너뜀` };
  if (needEl && !el) return { ok: false, text: `${cmd.raw} — 페이지에 없는 요소라 건너뜀` };
  const pi = el ? locateElement(el) : -1;
  if (needEl && pi < 0) return { ok: false, text: `${cmd.raw} — 이미 지워진 요소라 건너뜀` };
  const E = el as El;
  const done = (text: string) => {
    if (el && cmd.op !== '삭제') { showPage(pi); flashElement(el); }
    ed.bump();
    return { ok: true, text };
  };
  const notText = { ok: false, text: `${cmd.raw} — 텍스트가 아니라 건너뜀` };

  switch (cmd.op) {
    case '고치기':
      if (E.type !== 'text') return notText;
      return { later: true, task: { kind: 'rewrite', el: E, pi, how: vAll }, text: `${describeEl(E, pi)} 문구 다시 쓰기` };
    case '크기': {
      if (E.type !== 'text') return notText;
      let n = parseFloat(v);
      if (!n) n = (E.fontSize || 16) + (/(작|줄)/.test(v) ? -4 : 4);
      E.fontSize = Math.max(10, Math.min(96, Math.round(n)));
      return done(`${describeEl(E, pi)} 글자 크기 → ${E.fontSize}px`);
    }
    case '정렬': {
      if (E.type !== 'text') return notText;
      const al = /(가운데|중앙|center)/.test(v) ? 'center' : /(오른|우|right)/.test(v) ? 'right' : 'left';
      E.align = al;
      return done(`${describeEl(E, pi)} ${al === 'center' ? '가운데' : al === 'right' ? '오른쪽' : '왼쪽'} 정렬`);
    }
    case '굵게':
      if (E.type !== 'text') return notText;
      E.bold = !/(끄|해제|얇|false|off)/.test(v);
      return done(`${describeEl(E, pi)} ${E.bold ? '굵게' : '보통 굵기'}`);
    case '색': {
      const c = pickColor(vAll, TEXT_COLORS);
      if (!c) return { ok: false, text: `${cmd.raw} — 모르는 색이라 건너뜀` };
      if (E.type === 'text') E.color = c;
      else if (E.type === 'shape') E.bg = c;
      else return { ok: false, text: `${cmd.raw} — 사진은 색을 바꿀 수 없어요` };
      return done(`${describeEl(E, pi)} 색 → ${c}`);
    }
    case '삭제': {
      const label = describeEl(E, pi);
      showPage(pi);
      ed.pages[pi].elements = ed.pages[pi].elements.filter((e) => e !== E);
      if (ed.selectedId === E.id) ed.selectedId = null;
      return done(`${label} 삭제`);
    }
    case '확대': {
      if (E.type === 'text') return { ok: false, text: `${cmd.raw} — 글자는 "크기"로 바꿔요` };
      let f = parseFloat(v);
      if (!f || f <= 0) f = /(작|줄|축소)/.test(v) ? 0.8 : 1.2;
      f = Math.max(0.3, Math.min(3, f));
      E.w = Math.round(E.w * f); E.h = Math.round(E.h * f);
      return done(`${describeEl(E, pi)} 크기 ×${f}`);
    }
    case '대표사진': {
      if (E.type !== 'image') return { ok: false, text: `${cmd.raw} — 사진이 아니라 건너뜀` };
      const page = ed.pages[pi];
      const oldMain = roomParts(page).main;
      if (oldMain === E) return { ok: false, text: `${cmd.raw} — 이미 대표 사진이에요` };
      page.elements.filter((e) => e.type === 'image').forEach((e) => (e.role = e === E ? 'main' : 'sub'));
      if (isRoomPage(ed.pages, pi)) layoutRoomPage(page, page.layout || DEFAULT_ROOM_LAYOUT);
      else if (oldMain) {
        const g = [oldMain.x, oldMain.y, oldMain.w, oldMain.h];
        oldMain.x = E.x; oldMain.y = E.y; oldMain.w = E.w; oldMain.h = E.h;
        [E.x, E.y, E.w, E.h] = g;
      }
      return done(`${pi + 1}페이지 대표 사진 교체`);
    }
    case '사진수정':
      if (E.type !== 'image') return { ok: false, text: `${cmd.raw} — 사진이 아니라 건너뜀` };
      return { later: true, task: { kind: 'image', el: E, pi, how: after.join(' ') || msg }, text: `${describeEl(E, pi)} AI 편집` };
    case '사진맞춤': {
      if (E.type !== 'image') return { ok: false, text: `${cmd.raw} — 사진이 아니라 건너뜀` };
      const refTok = after.find((t) => /^[i]\d+[,.)]?$/.test(t));
      const ref = refTok ? ctx.alias[refTok.replace(/[^\w]/g, '')] : null;
      const rest = after.filter((t) => t !== refTok).join(' ');
      if (!ref || ref === E || ref.type !== 'image') return { later: true, task: { kind: 'image', el: E, pi, how: rest || msg }, text: `${describeEl(E, pi)} AI 편집` };
      return { later: true, task: { kind: 'match', el: E, ref, pi, how: rest }, text: `${describeEl(E, pi)} AI 편집` };
    }
    case '사진원복':
      if (E.type !== 'image') return { ok: false, text: `${cmd.raw} — 사진이 아니라 건너뜀` };
      if (!restorePhoto(E)) return { ok: false, text: `${describeEl(E, pi)} — AI로 고친 적 없는 원본 사진이라 그대로 둠` };
      return done(`${describeEl(E, pi)} 원본 사진으로 되돌림`);
    case '앞으로': bringToFront(E); return done(`${describeEl(E, pi)} 맨 앞으로`);
    case '뒤로': sendToBack(E); return done(`${describeEl(E, pi)} 맨 뒤로`);
    case '레이아웃': {
      const preset = /(띠|블로그|band)/.test(a1) ? 'band' : /(사이드|세로|side)/.test(a1) ? 'side' : /(그리드|격자|grid)/.test(a1) ? 'grid'
        : /(반쪽|분할|split)/.test(a1) ? 'split' : /(기본|크게|처음|원래|hero)/.test(a1) ? 'hero' : /(가운데|중앙|center)/.test(a1) ? 'center' : null;
      if (!preset) return { ok: false, text: `${cmd.raw} — 모르는 레이아웃이라 건너뜀` };
      const targets = /(전체|모든|모두|^0$)/.test(a0) ? ed.pages.map((_, i) => i).filter((i) => isRoomPage(ed.pages, i)) : [agentPageIndex(ctx, a0)].filter((i) => i >= 0);
      const changed = targets.filter((i) => isRoomPage(ed.pages, i) && layoutRoomPage(ed.pages[i], preset));
      if (!changed.length) return { ok: false, text: `${cmd.raw} — 공간 페이지가 아니라 건너뜀` };
      if (!changed.includes(ed.cur)) showPage(changed[0]);
      ed.bump();
      return { ok: true, text: `${changed.map((i) => i + 1).join('·')}페이지 레이아웃 → ${ROOM_LAYOUTS[preset]}` };
    }
    case '배경': {
      const c = pickColor(cmd.args.slice(a0.length), COLOR_WORDS);
      if (!c) return { ok: false, text: `${cmd.raw} — 모르는 색이라 건너뜀` };
      const targets = /(전체|모든|모두|^0$)/.test(a0) ? ed.pages.map((_, i) => i) : [agentPageIndex(ctx, a0)].filter((i) => i >= 0);
      if (!targets.length) return { ok: false, text: `${cmd.raw} — 없는 페이지라 건너뜀` };
      targets.forEach((i) => (ed.pages[i].bg = c));
      if (!targets.includes(ed.cur)) showPage(targets[0]);
      ed.bump();
      return { ok: true, text: targets.length > 1 ? `모든 페이지 배경 → ${c}` : `${targets[0] + 1}페이지 배경 → ${c}` };
    }
    case '글추가': {
      const tpi = agentPageIndex(ctx, a0);
      if (tpi < 0) return { ok: false, text: `${cmd.raw} — 없는 페이지라 건너뜀` };
      const heading = /제목/.test(a1);
      const how = cmd.args.split(/\s+/).slice(/^(제목|본문)$/.test(a1) ? 2 : 1).join(' ');
      return { later: true, task: { kind: 'add', pi: tpi, heading, how }, text: `${tpi + 1}페이지에 ${heading ? '제목' : '본문'} 추가` };
    }
    case '새페이지':
      ed.pages.push({ id: pageId(), bg: '#FFFFFF', elements: [] }); // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
      showPage(ed.pages.length - 1);
      return { ok: true, text: `빈 페이지 추가 (${ed.pages.length}페이지)` };
    case '페이지삭제': {
      const tpi = agentPageIndex(ctx, a0);
      if (tpi < 0) return { ok: false, text: `${cmd.raw} — 없는 페이지라 건너뜀` };
      if (ed.pages.length <= 1) return { ok: false, text: '마지막 남은 페이지는 지울 수 없어요' };
      ed.pages.splice(tpi, 1);
      ed.cur = Math.min(ed.cur >= tpi ? Math.max(0, ed.cur - 1) : ed.cur, ed.pages.length - 1);
      ed.selectedId = null;
      ed.bump();
      return { ok: true, text: `${tpi + 1}페이지 삭제 (남은 페이지 ${ed.pages.length}개)` };
    }
    case '이동': {
      const tpi = agentPageIndex(ctx, a0);
      if (tpi < 0) return { ok: false, text: `${cmd.raw} — 없는 페이지라 건너뜀` };
      showPage(tpi);
      return { ok: true, text: `${tpi + 1}페이지로 이동` };
    }
  }
  return { ok: false, text: `${cmd.raw} — 알 수 없는 명령` };
}

/* ── 글 쓰기 단계: 문체 프롬프트로 쓰면서 그 텍스트 상자에 실시간으로 흘려 넣는다 ── */
function buildRewritePrompt(el: El, how: string, msg: string): { direct?: string; prompt?: string; heading: boolean } {
  const heading = textRole(el) === '제목';
  const quoted = how.match(/^["'“‘「]([^"'”’」]+)["'”’」]/);
  if (quoted && msg.includes(quoted[1])) return { direct: quoted[1].trim(), heading };
  const req = how && !quoted && how.replace(/[\s.]/g, '').length >= 2 && !/^(수정|변경|고치기|다시\s*쓰기)$/.test(how.trim()) ? how : msg;
  return { prompt: heading ? buildHeadingEditPrompt(el.text || '', req) : buildElementEditPrompt(el.text || '', req), heading };
}
function buildAddTextPrompt(pi: number, heading: boolean, how: string, msg: string) {
  const page = ed.pages[pi];
  const topic = (page.elements.find((e) => e.role === 'heading') || page.elements.find(isHeading))?.text || '';
  const others = page.elements.filter((e) => e.type === 'text' && (e.text || '').trim()).map((e) => '- ' + (e.text || '').trim().slice(0, 120)).join('\n') || '(없음)';
  const what = heading ? '제목 한 줄(15자 이내, 문장부호 없이)' : '본문 문단 하나(5~6문장)';
  return `${WRITER_PERSONA}\n\n시공사례 ${topic ? `'${topic}' ` : ''}페이지에 새로 넣을 ${what}을 쓰세요. 결과만 출력하고 다른 설명은 붙이지 마세요.\n\n[이 페이지에 이미 있는 글]\n${others}\n\n[요청]\n${how || msg}\n(사용자가 채팅에 쓴 말: ${msg})`;
}
async function runWriterTask(task: Task, msg: string, aiMsg: ChatMsg) {
  let el: El, pi: number;
  if (task.kind === 'add') {
    pi = task.pi;
    const page = ed.pages[pi];
    const bottom = page.elements.reduce((m, e) => Math.max(m, e.y + e.h), 0);
    const h = task.heading ? 60 : 140;
    el = { id: newId(), type: 'text', role: task.heading ? 'heading' : 'body', x: 48, y: Math.min(bottom + 16, PAGE_H - 36 - h), w: 624, h,
      z: page.elements.length ? Math.max(...page.elements.map((e) => e.z)) + 1 : 1, text: '',
      fontSize: task.heading ? 30 : 15.5, bold: task.heading, color: task.heading ? '#1B1F4F' : '#374151', align: 'left' }; // dq-ignore: AI가 문서에 넣는 색(문서 데이터)
    page.elements.push(el);
  } else {
    el = task.el;
    pi = locateElement(el);
    if (pi < 0) throw new Error('그새 지워진 텍스트');
  }
  showPage(pi);
  flashElement(el);
  const original = el.text || '';
  const p = task.kind === 'add' ? { prompt: buildAddTextPrompt(pi, task.heading, task.how, msg), heading: task.heading } : buildRewritePrompt(el, task.how, msg);
  if ('direct' in p && p.direct) { liveSetText(el, p.direct); return; }
  setTyping(el, true, task.kind === 'add' ? '새 글' : '글 고치기');
  try {
    const imgs = pageImageRefs(pi);
    const r = await streamText({ projectId: ed.projectId, aiType: 'WRITER', prompt: withPhotosAndGuide(p.prompt!, imgs.length, guideText()), images: imgs, temperature: 0.6, reqKey: reqKey('w') },
      (partial) => liveSetText(el, cleanWriterText(partial, p.heading)), signal());
    addUsage(aiMsg, r.usage);
    const final = cleanWriterText(r.text, p.heading);
    if (!final) throw new Error('빈 응답');
    liveSetText(el, final);
    if (p.heading && pi === 0) ed.title = final;
  } catch (e) {
    if (task.kind === 'add') ed.pages[pi].elements = ed.pages[pi].elements.filter((x) => x !== el);
    else liveSetText(el, original);
    throw e;
  } finally {
    setTyping(el, false);
  }
}

/* ── 사진 편집: Gemini 이미지 모델이 사진 자체를 고친다 ── */
async function runImageTask(task: Extract<Task, { kind: 'image' | 'match' }>, msg: string, aiMsg: ChatMsg) {
  const el = task.el, pi = locateElement(el);
  if (pi < 0) throw new Error('그새 지워진 사진');
  showPage(pi);
  ed.imaging.add(el.id);
  ed.bump();
  const room = ed.pages[pi].room ? roomLabel(ed.pages[pi].room) : '';
  const tpl = currentTemplate();
  try {
    const source = refOf(el);
    if (!source) throw new Error('사진 정보가 없어요');
    const marked = task.kind === 'image' ? task.marked : undefined;
    const prompt = marked && task.kind === 'image' ? task.prompt || ''
      : task.refEl ? buildImageMatchPrompt(room, task.how, msg)
      : buildImageEditPrompt(room, task.how || msg, msg, tpl);
    const r = await showcaseApi.imageEdit({
      projectId: ed.projectId, source, originalPhotoId: el.origPhotoId ?? el.photoId ?? null, marked, prompt,
      ref: task.refEl ? refOf(task.refEl) : null, changeType: marked ? 'MARKUP' : task.refEl ? 'MATCH' : 'CHAT', reqKey: reqKey('i'),
    }, signal());
    addUsage(aiMsg, r.usage);
    // 새 사진을 다 받아 둔 뒤에 바꿔야 깜빡이지 않는다
    await new Promise((res) => { const im = new Image(); im.onload = im.onerror = res; im.src = r.url; });
    if (!el.origSrc) { el.origSrc = el.src; el.origPhotoId = el.photoId; el.origExternalId = el.externalId; } // 몇 번을 고쳐도 "원복"은 맨 처음 사진으로
    el.src = r.url; el.aiImgId = r.aiImgId;
    delete el.photoId; delete el.externalId;
  } finally {
    ed.imaging.delete(el.id);
    flashElement(el);
    ed.bump();
  }
}
/* 이번 요청의 사진 편집들을 첨부 순서로 세우고, 첫 사진을 고친 결과를 나머지의 기준으로 삼는다 */
function chainPhotoTasks(ctx: AgentCtx, tasks: { task: Task; entry: ChatLog }[], aiMsg: ChatMsg) {
  const isPhoto = (w: { task: Task }) => w.task.kind === 'image' || w.task.kind === 'match';
  const photos = tasks.filter(isPhoto);
  if (!photos.length) return;
  const order = (w: { task: Task }) => (ctx.scoped ? ctx.attached.indexOf(w.task.el as El) : tasks.indexOf(w as { task: Task; entry: ChatLog }));
  photos.sort((a, b) => order(a) - order(b));
  const label = (el: El) => (ctx.scoped ? `${ctx.attached.indexOf(el) + 1}번 사진` : describeEl(el, locateElement(el)));
  let base: El | null = null;
  photos.forEach((w) => {
    const t = w.task as Extract<Task, { kind: 'image' | 'match' }>;
    if (t.kind === 'match') { t.refEl = t.ref; if (!base) base = t.ref; }
    else if (base && base !== t.el) t.refEl = base;
    else if (!base) base = t.el;
    w.entry.text = t.refEl ? `${label(t.el)} — ${label(t.refEl)} 가구에 맞춰 AI 편집` : `${label(t.el)} AI 편집`;
  });
  const rest = tasks.filter((w) => !isPhoto(w));
  tasks.length = 0;
  tasks.push(...rest, ...photos);
  const entries = new Set(photos.map((w) => w.entry));
  aiMsg.log = (aiMsg.log || []).filter((l) => !entries.has(l)).concat(photos.map((w) => w.entry));
}
function isQuestionOnly(msg: string) {
  return /(\?|어때|좋을까|할까|뭘|뭐가|무엇|어떤\s|어떻게\s*(?:하면|생각)|추천|피드백|평가|의견|보완)/.test(msg)
    && !/(해\s*줘|해주세요|바꿔|고쳐|써\s*줘|넣어|빼\s*줘|지워|줄여|키워|옮겨|적용)/.test(msg);
}
/** attached 가 있으면 그 요소만 보여주는 첨부 모드, 없으면 페이지 전체 */
async function runShowcaseAgent(msg: string, attached: El[]) {
  const ctx = attached.length ? buildScopedContext(attached) : buildAgentContext();
  const aiMsg: ChatMsg = { role: 'ai', text: '', log: [], status: 'AI가 요청을 읽고 있어요…' };
  ed.chat.push(aiMsg);
  ed.bump();
  const questionOnly = isQuestionOnly(msg);
  const replyParts: string[] = [];
  let handled = 0, sawCommand = false;
  const seen = new Set<string>(), tasks: { task: Task; entry: ChatLog }[] = [];
  const takeLine = (raw: string) => {
    const line = raw.trim().replace(/^답\s*[:：]\s*/, '');
    if (!line) return;
    let cmd = parseAgentLine(line);
    // 첫 줄은 답 — 단, "정렬 t3 가운데"처럼 id·페이지 번호가 붙은 명령 모양이면 답 없이 바로 명령을 쓴 것으로 본다
    if (cmd && !replyParts.length && !sawCommand && !/^\S+\s+(?:[tis]\d+|\d+|전체)\b|^새페이지$/.test(cmd.raw)) cmd = null;
    if (!cmd) { if (!sawCommand) replyParts.push(line); return; }
    sawCommand = true;
    if (questionOnly) return;
    if (seen.has(cmd.raw)) return;
    seen.add(cmd.raw);
    const r = applyAgentCommand(ctx, cmd, msg);
    if (r.later) {
      if (r.task.el && tasks.some((w) => w.task.el === r.task.el)) return;
      const entry: ChatLog = { state: 'wait', text: r.text };
      aiMsg.log!.push(entry);
      tasks.push({ task: r.task, entry });
    } else aiMsg.log!.push({ state: r.ok ? 'ok' : 'skip', text: r.text });
  };
  const onText = (full: string, final: boolean) => {
    const lines = full.split('\n');
    const complete = final ? lines : lines.slice(0, -1);
    for (; handled < complete.length; handled++) takeLine(complete[handled]);
    const tail = final ? '' : lines[lines.length - 1].trim().replace(/^답\s*[:：]\s*/, '');
    aiMsg.text = replyParts.join(' ') + (!sawCommand && !replyParts.length && tail ? tail : '');
    if (aiMsg.text) aiMsg.status = sawCommand ? '페이지를 고치는 중…' : '';
    ed.bump();
  };
  let stopped = false, lastText = '', cutEarly = false;
  // 질문인데 모델이 명령을 쓰기 시작하면 거기서 생성을 끊는다
  const local = new AbortController();
  ed.abort?.signal.addEventListener('abort', () => local.abort());
  try {
    let out: string;
    try {
      const r = await streamText({ projectId: ed.projectId, aiType: 'AGENT', prompt: buildAgentPrompt(ctx, msg), temperature: 0.2, reqKey: reqKey('a') },
        (t) => { lastText = t; onText(t, false); if (questionOnly && sawCommand && !cutEarly) { cutEarly = true; local.abort(); } }, local.signal);
      addUsage(aiMsg, r.usage);
      out = r.text;
    } catch (e) {
      if (!(cutEarly && isAbort(e) && !ed.abort?.signal.aborted)) throw e;
      out = lastText;
    }
    onText(out, true);
    chainPhotoTasks(ctx, tasks, aiMsg);
    for (let i = 0; i < tasks.length; i++) {
      const { task, entry } = tasks[i];
      entry.state = 'run';
      const photo = task.kind === 'image' || task.kind === 'match';
      aiMsg.status = (photo && task.refEl ? 'AI가 기준 사진에 맞춰 사진을 고치는 중… (15~20초)' : photo ? 'AI가 사진을 고치는 중… (10~20초)' : '문구를 쓰는 중…') + ` (${i + 1}/${tasks.length})`;
      ed.bump();
      try {
        if (photo) await runImageTask(task, msg, aiMsg);
        else await runWriterTask(task, msg, aiMsg);
        entry.state = 'ok';
      } catch (e) {
        if (isAbort(e)) throw e;
        entry.state = 'fail';
        entry.text += ` — 실패(${errorMessage(e)})`;
      }
      ed.bump();
    }
  } catch (e) {
    if (isAbort(e)) stopped = true;
    else { aiMsg.status = ''; aiMsg.log!.push({ state: 'fail', text: 'AI 호출 실패 — ' + errorMessage(e) }); }
  }
  tasks.forEach(({ entry }) => { if (entry.state === 'wait' || entry.state === 'run') { entry.state = 'skip'; entry.text += ' — 중지됨'; } });
  if (!aiMsg.text) aiMsg.text = aiMsg.log!.length ? '요청하신 내용을 반영했어요.' : '답을 만들지 못했어요. 조금 다르게 말해 주시겠어요?';
  const applied = aiMsg.log!.filter((l) => l.state === 'ok').length;
  if (stopped) aiMsg.status = `중지했어요.${applied ? ` 이미 바뀐 ${applied}개는 "되돌리기"로 되돌릴 수 있어요.` : ''}`;
  else if (questionOnly) aiMsg.status = '';
  else if (!applied && sawCommand) aiMsg.status = '실제로 바뀐 것은 없어요. 페이지나 요소를 조금 더 구체적으로 말해 주세요.';
  else if (!applied && /(게요|겠습니다|했어요|했습니다|드릴게요|드렸)/.test(aiMsg.text)) aiMsg.status = '※ 이번 답에는 실제 변경 명령이 없었어요. 다시 한번 말해 주시면 적용해 볼게요.';
  else aiMsg.status = '';
  ed.bump();
}

/* ============================================================
   채팅 진입점
   ============================================================ */
const UNDO_WORDS = /^(되돌려|되돌리|실행\s*취소|원상\s*복구|방금\s*(?:거|것|변경|수정)\s*(?:취소|되돌))/;
export async function sendChat(msg: string) {
  if (ed.busy) { ed.abort?.abort(); return; }
  msg = msg.trim();
  if (!msg) return;
  ed.picking = false;
  ed.attached = ed.attached.filter((e) => locateElement(e) >= 0);
  const attached = ed.attached.slice(); // 보낸 시점의 첨부 — 이번 요청의 대상
  ed.chat.push({ role: 'user', text: msg, attach: attached.map(attachLabel) });
  ed.attached = []; // 보내면 첨부는 비운다 — 무엇을 첨부했는지는 보낸 메시지의 첨부 표시에 남는다
  ed.bump();
  if (UNDO_WORDS.test(msg)) { undoLast(); return; }
  if (!ed.pages.length) { ed.chat.push({ role: 'system', text: '먼저 페이지를 만들어주세요.' }); ed.bump(); return; }
  ed.abort = new AbortController();
  setBusy(true);
  try {
    await withUndo(() => runShowcaseAgent(msg, attached));
  } finally {
    setBusy(false);
    void saveNow();
  }
}

/** 표시하고 고치기 — 표시를 그린 사진(data URL)과 표시별 요청으로 AI 편집 */
export async function submitMarkup(el: El, marked: string, marks: Mark[], how: string) {
  if (ed.busy) { toast('AI가 작업 중이에요. 끝나거나 ■ 중지한 뒤에 해 주세요.'); return; }
  const room = (() => { const p = ed.pages[locateElement(el)]; return p?.room ? roomLabel(p.room) : ''; })();
  const prompt = buildMarkupPrompt(room, marks, how, currentTemplate());
  ed.dockOpen = true;
  const pi = locateElement(el);
  const label = `${pi + 1}p ${photoName(el)}`;
  const noteTxt = marks.map((m, i) => `${i + 1}. ${(m.note || '').trim() || '(전체 요청)'}`).join(' · ');
  ed.chat.push({ role: 'user', text: `표시 ${marks.length}곳으로 사진 고치기 — ${noteTxt}${how ? ` / ${how}` : ''}`, attach: [label] });
  ed.abort = new AbortController();
  setBusy(true);
  try {
    await withUndo(async () => {
      const aiMsg: ChatMsg = { role: 'ai', text: `표시한 ${marks.length}곳을 요청대로 고칠게요.`, log: [{ state: 'run', text: `${label} — 표시 ${marks.length}곳 반영해 AI 편집` }], status: 'AI가 사진을 고치는 중… (10~20초)' };
      ed.chat.push(aiMsg);
      ed.bump();
      try {
        await runImageTask({ kind: 'image', el, pi, how, marked, prompt }, prompt, aiMsg);
        aiMsg.log![0].state = 'ok';
        aiMsg.status = '마음에 안 들면 "원복시켜줘"나 ↩ 되돌리기로 원래 사진으로 돌아가요.';
      } catch (e) {
        aiMsg.log![0].state = isAbort(e) ? 'skip' : 'fail';
        aiMsg.log![0].text += isAbort(e) ? ' — 중지됨' : ` — 실패(${errorMessage(e)})`;
        aiMsg.status = '';
      }
      ed.bump();
    });
  } finally {
    setBusy(false);
    void saveNow();
  }
}
